import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { relatoriosService, Relatorio, FiltrosRelatorio } from '../../services/relatoriosService';

interface RelatoriosContextData {
  relatorios: Relatorio[];
  loading: boolean;
  filtros: FiltrosRelatorio;
  abaAtiva: string;
  filtrosAbertos: boolean;
  unidades: Array<{ label: string; value: string }>;
  tiposRelatorio: Array<{ label: string; value: string }>;
  statusOperacional: Array<{ label: string; value: string }>;
  setFiltros: (filtros: Partial<FiltrosRelatorio>) => void;
  setAbaAtiva: (aba: string) => void;
  toggleFiltros: () => void;
  limparFiltros: () => void;
  aplicarFiltros: () => void;
  gerarRelatorio: () => void;
  baixarArquivo: (id: string) => void;
  preVisualizarExcel: (id: string) => void;
  visualizarDados: (id: string) => void;
  setUnidades: (unidades: Array<{ label: string; value: string }>) => void;
  setTiposRelatorio: (tipos: Array<{ label: string; value: string }>) => void;
  setStatusOperacional: (status: Array<{ label: string; value: string }>) => void;
}

const filtrosIniciais: FiltrosRelatorio = {
  pesquisa: '',
  unidade: 'Matriz - Hospital Israelita Albert Einsten - 60.765.823/0001-30',
  tipoRelatorio: '',
  statusOperacional: 'Em processamento',
  periodoInicio: '01/07/2026',
  periodoFim: '06/07/2026',
  valorInicio: 'R$ 2.800,00',
  valorFim: 'R$ 3.000,00'
};

const RelatoriosContext = createContext<RelatoriosContextData>({} as RelatoriosContextData);

export function RelatoriosProvider({ children }: { children: ReactNode }) {
  const [relatorios, setRelatorios] = useState<Relatorio[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltrosState] = useState<FiltrosRelatorio>(filtrosIniciais);
  const [abaAtiva, setAbaAtiva] = useState('todos');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [unidades, setUnidades] = useState<Array<{ label: string; value: string }>>([]);
  const [tiposRelatorio, setTiposRelatorio] = useState<Array<{ label: string; value: string }>>([]);
  const [statusOperacionalOpts, setStatusOperacionalOpts] = useState<Array<{ label: string; value: string }>>([]);

  useEffect(() => {
    Promise.all([
      relatoriosService.listar(),
      relatoriosService.obterUnidades(),
      relatoriosService.obterTiposRelatorio(),
      relatoriosService.obterStatusOperacional()
    ]).then(([rels, units, tipos, statusOpts]) => {
      setRelatorios(rels);
      setUnidades(units as Array<{ label: string; value: string }>);
      setTiposRelatorio(tipos as Array<{ label: string; value: string }>);
      setStatusOperacionalOpts(statusOpts as Array<{ label: string; value: string }>);
      setLoading(false);
    });
  }, []);

  const setFiltros = (novosFiltros: Partial<FiltrosRelatorio>) => {
    setFiltrosState(prev => ({ ...prev, ...novosFiltros }));
  };

  const toggleFiltros = () => setFiltrosAbertos(prev => !prev);

  const limparFiltros = () => setFiltrosState(filtrosIniciais);

  const aplicarFiltros = async () => {
    setLoading(true);
    const rels = await relatoriosService.listar();
    setRelatorios(rels);
    setLoading(false);
  };

  const gerarRelatorio = async () => {
    await relatoriosService.gerarRelatorio();
    const rels = await relatoriosService.listar();
    setRelatorios(rels);
  };

  const baixarArquivo = async (id: string) => {
    await relatoriosService.baixarArquivo(id);
  };

  const preVisualizarExcel = async (id: string) => {
    await relatoriosService.preVisualizarExcel(id);
  };

  const visualizarDados = (id: string) => {
    console.log('Visualizar dados:', id);
  };

  return (
    <RelatoriosContext.Provider
      value={{
        relatorios,
        loading,
        filtros,
        abaAtiva,
        filtrosAbertos,
        unidades,
        tiposRelatorio,
        statusOperacional: statusOperacionalOpts,
        setFiltros,
        setAbaAtiva,
        toggleFiltros,
        limparFiltros,
        aplicarFiltros,
        gerarRelatorio,
        baixarArquivo,
        preVisualizarExcel,
        visualizarDados,
        setUnidades,
        setTiposRelatorio,
        setStatusOperacional: setStatusOperacionalOpts
      }}
    >
      {children}
    </RelatoriosContext.Provider>
  );
}

export const useRelatorios = () => useContext(RelatoriosContext);
